import { useState, useEffect } from 'react';
import { useCategories } from '../../../hooks/useCategories';
import type { Payee, PayeePayload } from '../../../hooks/usePayees';

interface Props {
  open: boolean;
  payee?: Payee | null;
  onClose: () => void;
  onSave: (payload: PayeePayload) => Promise<{ success: boolean; message: string }>;
}

const EMPTY: PayeePayload = {
  name: '', type: 'pj', document: null, nickname: null,
  phone: null, email: null, notes: null, is_active: true,
  default_category: null, default_subcategory: null,
};

const inputCls = 'w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-50';
const labelCls = 'text-xs font-600 text-gray-500 uppercase tracking-wide mb-1.5 block';

/** Mini category picker — shows pills from DB, no inline creation */
const CategoryPills = ({
  value, onChange,
}: { value: string | null; onChange: (v: string | null) => void }) => {
  const { categories: expCats, loading: lExp } = useCategories('expense');
  const { categories: incCats, loading: lInc } = useCategories('income');
  const [tab, setTab] = useState<'expense' | 'income'>('expense');
  const cats = tab === 'expense' ? expCats : incCats;
  const loading = lExp || lInc;

  return (
    <div className="space-y-2">
      {/* Tab */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit">
        {(['expense', 'income'] as const).map(t => (
          <button key={t} type="button" onClick={() => setTab(t)}
            className={`px-3 py-1 text-[11px] font-600 rounded-lg transition-all cursor-pointer whitespace-nowrap ${tab === t ? 'bg-white text-gray-900' : 'text-gray-500'}`}>
            {t === 'expense' ? 'Despesa' : 'Receita'}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex gap-1.5 flex-wrap">{[1,2,3,4].map(i => <div key={i} className="h-7 w-20 bg-gray-100 rounded-xl animate-pulse" />)}</div>
      ) : (
        <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto">
          {/* None option */}
          <button type="button" onClick={() => onChange(null)}
            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-xl border text-xs font-600 transition-all cursor-pointer whitespace-nowrap ${
              value === null ? 'border-gray-400 bg-gray-100 text-gray-700' : 'border-gray-200 text-gray-400 hover:border-gray-300'
            }`}>
            <i className="ri-close-line text-sm" />Nenhuma
          </button>
          {cats.map(cat => {
            const sel = value === cat.name;
            return (
              <button key={cat.id} type="button" onClick={() => onChange(sel ? null : cat.name)}
                style={sel ? { borderColor: cat.color, backgroundColor: cat.color + '20', color: cat.color } : undefined}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl border text-xs font-600 transition-all cursor-pointer whitespace-nowrap ${
                  sel ? '' : 'border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-gray-50'
                }`}>
                {cat.icon && <i className={`${cat.icon} text-sm`} style={sel ? { color: cat.color } : undefined} />}
                {cat.name}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const PayeeModal = ({ open, payee, onClose, onSave }: Props) => {
  const [form, setForm] = useState<PayeePayload>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setForm(payee ? {
        name: payee.name, type: payee.type, document: payee.document,
        nickname: payee.nickname, phone: payee.phone, email: payee.email,
        notes: payee.notes, is_active: payee.is_active,
        default_category: payee.default_category ?? null,
        default_subcategory: payee.default_subcategory ?? null,
      } : EMPTY);
      setError('');
    }
  }, [open, payee]);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('Nome é obrigatório.'); return; }
    setSaving(true);
    const res = await onSave(form);
    setSaving(false);
    if (!res.success) { setError(res.message); return; }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl max-w-md w-full mx-4 max-h-[92vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <h3 className="text-base font-700 text-gray-900">{payee ? 'Editar Favorecido' : 'Novo Favorecido'}</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer">
            <i className="ri-close-line" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {error && (
            <div className="flex items-center gap-2 bg-red-50 text-red-700 text-sm px-3 py-2.5 rounded-xl">
              <i className="ri-error-warning-line" />{error}
            </div>
          )}

          {/* Type */}
          <div>
            <label className={labelCls}>Tipo</label>
            <div className="grid grid-cols-2 gap-2">
              {(['pj', 'pf'] as const).map(t => (
                <button key={t} type="button" onClick={() => setForm(p => ({ ...p, type: t }))}
                  className={`py-2.5 rounded-xl border text-sm font-600 transition-all cursor-pointer whitespace-nowrap ${
                    form.type === t ? 'border-emerald-400 bg-emerald-50 text-emerald-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'
                  }`}>
                  {t === 'pj' ? 'Pessoa Jurídica' : 'Pessoa Física'}
                </button>
              ))}
            </div>
          </div>

          {/* Name */}
          <div>
            <label className={labelCls}>Nome *</label>
            <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
              placeholder={form.type === 'pj' ? 'Ex: Netflix, Academia, Aluguel...' : 'Nome completo'}
              className={inputCls} />
          </div>

          {/* Nickname */}
          <div>
            <label className={labelCls}>Apelido <span className="font-400 text-gray-400 normal-case">(opcional)</span></label>
            <input value={form.nickname ?? ''} onChange={e => setForm(p => ({ ...p, nickname: e.target.value || null }))}
              placeholder="Ex: Streaming de filmes" className={inputCls} />
          </div>

          {/* Document */}
          <div>
            <label className={labelCls}>{form.type === 'pj' ? 'CNPJ' : 'CPF'} <span className="font-400 text-gray-400 normal-case">(opcional)</span></label>
            <input value={form.document ?? ''} onChange={e => setForm(p => ({ ...p, document: e.target.value || null }))}
              placeholder={form.type === 'pj' ? '00.000.000/0001-00' : '000.000.000-00'} className={inputCls} />
          </div>

          {/* Phone + Email */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Telefone <span className="font-400 text-gray-400 normal-case">(opcional)</span></label>
              <input value={form.phone ?? ''} onChange={e => setForm(p => ({ ...p, phone: e.target.value || null }))}
                placeholder="(11) 9 9999-9999" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>E-mail <span className="font-400 text-gray-400 normal-case">(opcional)</span></label>
              <input type="email" value={form.email ?? ''} onChange={e => setForm(p => ({ ...p, email: e.target.value || null }))}
                placeholder="contato@empresa.com" className={inputCls} />
            </div>
          </div>

          {/* Default Category */}
          <div className="bg-gray-50 rounded-2xl border border-gray-100 p-4 space-y-3">
            <div className="flex items-center gap-2">
              <i className="ri-price-tag-3-line text-emerald-500 text-base" />
              <div>
                <p className="text-xs font-700 text-gray-700">Categoria padrão</p>
                <p className="text-[11px] text-gray-400">Preenchida automaticamente nos lançamentos deste favorecido</p>
              </div>
              {form.default_category && (
                <span className="ml-auto text-[10px] font-600 px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full shrink-0">
                  {form.default_category}
                </span>
              )}
            </div>

            <CategoryPills
              value={form.default_category}
              onChange={cat => setForm(p => ({ ...p, default_category: cat, default_subcategory: null }))}
            />

            {/* Subcategory */}
            {form.default_category && (
              <div>
                <label className="text-[11px] font-600 text-gray-500 uppercase tracking-wide mb-1 block">
                  Subcategoria padrão <span className="font-400 normal-case text-gray-400">(opcional)</span>
                </label>
                <input
                  value={form.default_subcategory ?? ''}
                  onChange={e => setForm(p => ({ ...p, default_subcategory: e.target.value || null }))}
                  placeholder={`Ex: Delivery, Streaming, Academia...`}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-xs focus:outline-none focus:border-emerald-400 bg-white"
                />
              </div>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className={labelCls}>Observação <span className="font-400 text-gray-400 normal-case">(opcional)</span></label>
            <textarea value={form.notes ?? ''} onChange={e => setForm(p => ({ ...p, notes: e.target.value || null }))}
              rows={2} maxLength={500} placeholder="Informações adicionais..."
              className={`${inputCls} resize-none`} />
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 border border-gray-200 text-gray-600 font-600 rounded-xl text-sm hover:bg-gray-50 cursor-pointer whitespace-nowrap">
              Cancelar
            </button>
            <button type="submit" disabled={saving}
              className="flex-1 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 text-white font-600 rounded-xl text-sm cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
              {saving && <i className="ri-loader-4-line animate-spin" />}
              {saving ? 'Salvando...' : payee ? 'Salvar' : 'Criar Favorecido'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
